/**
 * A pílula do relógio da sessão (docs/spec/session-clock.md), no fim da faixa de abas do chat da
 * mesa: o tempo de jogo correndo para todos e, para quem administra, começar, pausar, voltar e
 * encerrar. Encerrar pergunta ali mesmo. Logo depois do fim, o ★ Avaliar leva às avaliações da sessão.
 */
import { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/index.js';
import { useApp } from '../../store.js';
import { Icon } from '../../ui/icons.js';
import { useIsRoomGm } from '../audio/MusicPlayerModule.js';
import type { GameSessionAction, SessionRules } from '../../shared.js';
import { gameSessionTimes, roleOfModes, sessionRules } from '../../shared.js';
import { clockDetails, formatClock, formatDuration, pillView, playAnchor, type RoomClock } from './clockModel.js';
import { useServerNow } from './useServerNow.js';
import './clock.css';

/** Depois de um clique, os botões esperam o estado novo (ou isto): o duplo clique não desfaz o passo. */
const BUSY_MS = 600;
/** O "Encerrar" da pergunta ignora cliques logo que aparece (o segundo clique no ■ cairia nele). */
const CONFIRM_ARM_MS = 400;

/** O relógio da mesa da tela. */
export function useRoomClock(): RoomClock | null {
  return useApp((s) => s.openRooms.find((open) => open.roomId === s.activeRoomId)?.clock ?? null);
}

/** O registro das sessões e os relógios que a mesa da tela mostra (docs/spec/session-clock.md, "Ligar e desligar"). */
export function useSessionRules(): SessionRules {
  return sessionRules(useApp((s) => s.room));
}

/**
 * O ★ do fim da sessão (docs/spec/session-clock.md, "Avaliações"): "Avaliar a sessão" para quem está
 * com o papel de jogador, "Avaliar os jogadores" para o mestre; null para os outros, ou com o servidor
 * sem avaliações. O servidor confere de verdade quem jogou a sessão.
 */
export function useRateLabel(admin: boolean): string | null {
  const t = useT();
  const enabled = useApp((s) => s.serverFeatures.sessionRatings);
  const player = useApp((s) => !!s.user && roleOfModes(s.members[s.user.id]?.modes ?? []) === 'player');
  if (!enabled) return null;
  if (admin) return t('clock.rate.players');
  return player ? t('clock.rate.session') : null;
}

export function ClockPill() {
  const t = useT();
  const clock = useRoomClock();
  const enabled = useApp((s) => s.serverFeatures.gameSessions);
  const online = useApp((s) => s.status === 'online');
  const control = useApp((s) => s.controlGameSession);
  const openModule = useApp((s) => s.requestOpenModule);
  const focusSession = useApp((s) => s.focusGameSession);
  const roomId = useApp((s) => s.activeRoomId);
  const rules = useSessionRules();
  const admin = useIsRoomGm();
  const rateLabel = useRateLabel(admin);
  const view = pillView(clock, admin, enabled && rules.record && rules.chatClock);
  const session = view.kind === 'open' || view.kind === 'ended' ? view.session : null;
  const running = session?.status === 'running';
  const now = useServerNow(running, 1000, session ? playAnchor(session) : 0);

  // a trava do clique e a pergunta de encerrar valem para um estado da sessão: chegou outro, somem
  const stateKey = session ? `${session.id}:${session.status}` : view.kind;
  const [busyFor, setBusyFor] = useState<string | null>(null);
  const [confirmFor, setConfirmFor] = useState<string | null>(null);
  const busy = busyFor === stateKey;
  const confirming = confirmFor === stateKey;
  const setConfirming = (on: boolean) => setConfirmFor(on ? stateKey : null);
  const armedAt = useRef(0);
  const keepRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!busyFor) return;
    const timer = window.setTimeout(() => setBusyFor(null), BUSY_MS);
    return () => window.clearTimeout(timer);
  }, [busyFor]);
  useEffect(() => {
    if (!confirming) return;
    keepRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConfirmFor(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirming]);

  if (view.kind === 'none') return null;
  const off = !online || busy;
  const send = (action: GameSessionAction) => {
    if (off) return;
    if (control(action)) setBusyFor(stateKey);
  };

  if (view.kind === 'start') {
    return (
      <button type="button" className="rd-clock rd-clock--start" disabled={off} onClick={() => send('start')}>
        <Icon name="play" size={11} strokeWidth={0} filled />
        {t('clock.start', { n: view.number })}
      </button>
    );
  }

  if (view.kind === 'ended') {
    const played = formatDuration(t, view.session.playedMs);
    return (
      <div className="rd-clock rd-clock--ended" role="group" aria-label={t('clock.label')}>
        <button type="button" className="rd-clock__open" title={t('sessions.open')} onClick={() => openModule('system/gameSessions')}>
          <Icon name="stop" size={10} strokeWidth={0} filled />
          <span className="rd-clock__summary">{t('clock.ended', { n: view.session.number, played })}</span>
        </button>
        {rateLabel && roomId ? (
          <button type="button" className="rd-clock__rate" aria-label={rateLabel} title={rateLabel} onClick={() => focusSession(roomId, view.session.number)}>
            <Icon name="star" size={11} strokeWidth={2.2} />
            {t('clock.rate')}
          </button>
        ) : null}
        {view.next ? (
          <button type="button" className="rd-clock__btn" disabled={off} aria-label={t('clock.start', { n: view.next })} title={t('clock.start', { n: view.next })} onClick={() => send('start')}>
            <Icon name="play" size={11} strokeWidth={0} filled />
          </button>
        ) : null}
      </div>
    );
  }

  const { session: open, paused, controls } = view;
  const { playedMs } = gameSessionTimes(open, now);

  if (confirming && controls) {
    return (
      <div className="rd-clock rd-clock--confirm" role="group" aria-label={t('clock.end')}>
        <span>{t('clock.end.ask')}</span>
        <button
          type="button"
          className="rd-clock__yes"
          disabled={off}
          onClick={() => {
            if (performance.now() - armedAt.current >= CONFIRM_ARM_MS) send('end');
          }}
        >
          {t('clock.end.yes')}
        </button>
        <button type="button" ref={keepRef} className="rd-clock__btn" aria-label={t('clock.end.no')} title={t('clock.end.no')} onClick={() => setConfirming(false)}>
          <Icon name="close" size={11} strokeWidth={2.4} />
        </button>
      </div>
    );
  }

  const played = formatDuration(t, playedMs);
  return (
    <div className={`rd-clock ${paused ? 'rd-clock--paused' : 'rd-clock--running'}`} role="group" aria-label={t('clock.label')} title={clockDetails(t, open, now)}>
      {/* o tempo abre a aba Sessões (docs/spec/session-clock.md), para todos */}
      <button
        type="button"
        className="rd-clock__open"
        aria-label={t('sessions.open.label', { clock: t(paused ? 'clock.timer.paused' : 'clock.timer', { n: open.number, played }) })}
        onClick={() => openModule('system/gameSessions')}
      >
        <span className="rd-clock__dot" aria-hidden="true" />
        <span className="rd-clock__time">{formatClock(playedMs)}</span>
        {paused ? <span className="rd-clock__label">{t('clock.paused')}</span> : null}
      </button>
      {controls ? (
        <>
          <button
            type="button"
            className="rd-clock__btn"
            disabled={off}
            aria-label={t(paused ? 'clock.resume' : 'clock.pause')}
            title={t(paused ? 'clock.resume' : 'clock.pause')}
            onClick={() => send(paused ? 'resume' : 'pause')}
          >
            <Icon name={paused ? 'play' : 'pause'} size={11} strokeWidth={0} filled />
          </button>
          <button
            type="button"
            className="rd-clock__btn"
            disabled={off}
            aria-label={t('clock.end')}
            title={t('clock.end')}
            onClick={() => {
              armedAt.current = performance.now();
              setConfirming(true);
            }}
          >
            <Icon name="stop" size={10} strokeWidth={0} filled />
          </button>
        </>
      ) : null}
    </div>
  );
}
